import { Dispatch, FC, SetStateAction } from "react";
import type { PickInfo } from "@deck.gl/core/lib/deck";
import { RGBAColor } from "@deck.gl/core";
import { useMapLayer } from "react-migration/lib/map/useMapLayer";
import type { ClickedLatLng } from "react-migration/lib/map/typings";
import { ENVIRONMENT } from "src/js/util/environment";
import { FeatureFilter, OWNERSHIP_LAYERS, OwnershipLayer } from "./OwnershipLayer";
import {
  SATELLITE_LINE_COLOR,
  STREETMAP_LINE_COLOR,
} from "react-migration/domains/ownership/util/Colors";
import { useSelectedTitlePolyQuery } from "../../apollo/useTitlePolysByLocation";
import type { Geometry, OwnershipFeature } from "../../typings";
import { Stores, useGlobalState } from "react-migration/lib";

interface OwnershipLayerComponentProps {
  /** Hide the tile layer */
  hideVisualisation?: boolean;
  /** Fill parcels with colors based on proprietorship value */
  displayKnownOwners?: boolean;
  /** Hide parcels below this size (msq) */
  parcelSizeMin?: number;
  /** Hide parcels above this size (msq) */
  parcelSizeMax?: number;
  /** Are parcels intractable when clicked on */
  pickable?: boolean;
  /** Minimum zoom level to display parcels (deck.gl value is 1 lower than google maps) */
  minZoom?: number;
  /** Maximum zoom level to display parcels (deck.gl value is 1 lower than google maps) */
  maxZoom?: number;
  /** @deprecated */
  legacySelectedTitleGeometries?: { geometry: Geometry }[];
  /** Dictates order of layer in deck.gl's top level layer list */
  zOrder?: number;
  /** Width of the parcel boundary lines */
  lineWidth?: number;
  /** RGBA Color for the parcel boundaries */
  lineColor?: RGBAColor;
  /** RGBA Color for the parcel boundaries */
  boundaryOpacity?: number;
  /** Control fill opacity via multiplier. Defaults to 1  */
  fillOpacityMultiplier?: number;
  /** Lat/Lng of map selection used for selecting the polygon  */
  clickedLatLng?: ClickedLatLng;
  /** Higlight parcels intersecting clickedLatLng */
  highlightIntersections?: boolean;
  /** Returns event and latlng when parcel is selected  */
  onClickParcel?: (event: HammerInput, latLng: ClickedLatLng) => void;
  /** Returns feature and latlng when parcel is hovered  */
  onHoverParcel?: (d: OwnershipFeature, latLng: ClickedLatLng) => void;
  /**Change the view of the sidebar, summary or details or units */
  setSelectedView?: Dispatch<SetStateAction<string>>;
  /**Used to filter ownership features. If not provided, a default function is used which is always true. */
  featureFilter?: FeatureFilter;
}

const DEFAULT_HIDE_VISUALISATION = false;
const DEFAULT_HIGHLIGHT_INTERSECTIONS = true;
const DEFAULT_DISPLAY_KNOWN_OWNERS = false;
const DEFAULT_PICKABLE = true;
const DEFAULT_PARCEL_SIZE_MIN = 0;
const DEFAULT_PARCEL_SIZE_MAX = 1_000_000_000;
const DEFAULT_MIN_ZOOM = 12;
const DEFAULT_MAX_ZOOM = 18;
const DEFAULT_LINE_WIDTH = 1;
const DEFAULT_GEOMETRY_SELECTION: Geometry[] = [];
const OWNERSHIP_VEC_TILE_API_URL_TEMPLATE = `${ENVIRONMENT.OWNERSHIP_SERVICE_URL}/tiles/{z}/{x}/{y}`;

const DEFAULT_FEATURE_FILTER = () => true;

/**
 * Component for displaying the Ownership layer on a map
 */
export const OwnershipLayerComponent: FC<OwnershipLayerComponentProps> = ({
  hideVisualisation = DEFAULT_HIDE_VISUALISATION,
  highlightIntersections = DEFAULT_HIGHLIGHT_INTERSECTIONS,
  displayKnownOwners = DEFAULT_DISPLAY_KNOWN_OWNERS,
  pickable = DEFAULT_PICKABLE,
  parcelSizeMin = DEFAULT_PARCEL_SIZE_MIN,
  parcelSizeMax = DEFAULT_PARCEL_SIZE_MAX,
  minZoom = DEFAULT_MIN_ZOOM,
  maxZoom = DEFAULT_MAX_ZOOM,
  legacySelectedTitleGeometries,
  lineColor,
  lineWidth = DEFAULT_LINE_WIDTH,
  boundaryOpacity = 1.0,
  fillOpacityMultiplier = 1,
  clickedLatLng,
  onClickParcel,
  onHoverParcel,
  setSelectedView,
  zOrder,
  featureFilter = DEFAULT_FEATURE_FILTER,
}) => {
  const [{ settings }] = useGlobalState(Stores.map);
  const { mapType } = settings;
  const { selectedPolygons } = useSelectedTitlePolyQuery(clickedLatLng);
  const tileUrl = OWNERSHIP_VEC_TILE_API_URL_TEMPLATE;

  useMapLayer(
    () => ({
      zOrder,
      layer: new OwnershipLayer({
        id: "ownership-layer",
        tileUrl,
        visible: !hideVisualisation,
        parcelSizeMin: parcelSizeMin ?? DEFAULT_PARCEL_SIZE_MIN,
        parcelSizeMax: parcelSizeMax ?? DEFAULT_PARCEL_SIZE_MAX,
        displayKnownOwners,
        featureFilter,
        pickable,
        minZoom,
        maxZoom,
        lineWidth,
        boundaryOpacity,
        fillOpacityMultiplier,
        lineColor:
          lineColor || mapType === "satellite" || mapType === "hybrid"
            ? SATELLITE_LINE_COLOR
            : STREETMAP_LINE_COLOR,
        selection:
          legacySelectedTitleGeometries?.map((x: { geometry: Geometry }) => x.geometry) ||
          (highlightIntersections ? selectedPolygons : DEFAULT_GEOMETRY_SELECTION),
        layers: [OWNERSHIP_LAYERS.OWNERSHIP],
        onClick: ({ coordinate, object }: PickInfo<OwnershipFeature>, event: HammerInput) => {
          if (coordinate) {
            if (typeof setSelectedView === "function") {
              setSelectedView("summary");
            }
            const [lng, lat] = coordinate;
            onClickParcel?.(event, { lat, lng, country: object?.properties?.country });
          }
        },
        onHover: (pickInfo: PickInfo<OwnershipFeature>) => {
          if (pickInfo.object && pickInfo.coordinate) {
            const [lng, lat] = pickInfo.coordinate;
            onHoverParcel?.(pickInfo.object, {
              lat,
              lng,
              country: pickInfo.object?.properties?.country,
            });
          }
        },
      }),
    }),
    [
      zOrder,
      tileUrl,
      hideVisualisation,
      parcelSizeMin,
      parcelSizeMax,
      displayKnownOwners,
      pickable,
      minZoom,
      maxZoom,
      lineWidth,
      boundaryOpacity,
      fillOpacityMultiplier,
      lineColor,
      mapType,
      legacySelectedTitleGeometries,
      highlightIntersections,
      selectedPolygons,
      setSelectedView,
      onClickParcel,
      onHoverParcel,
      featureFilter,
    ]
  );

  return null;
};
